import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { withStyles } from "material-ui/styles";
import Paper from "material-ui/Paper";
import { isFreePlan } from '../../../utils/planUtils';
import CheckBox from './parts/CheckBox';
import Logo from './parts/Logo';
import Level from './cols/Level';
import DepartmentTel from './cols/DepartmentTel';
import Actions from './cols/Actions';
import KeymanAttribute from './cols/KeymanAttribute';
import IntentKeywords from './cols/IntentKeywords';

const styles = () => ({
  root: {
    display: "flex",
    alignItems: "center",
    padding: "1px",
    boxSizing: "border-box",
    width: "100%",
    "&:hover": {
      background: "rgb(26, 101, 255, 0.05)",
    },
    overflow: "hidden",
    willChange: "transform",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
  },

  hasBorderTop: {
    borderTop: "1px solid #D8DBDF",
  },

  containerChecked: {
    background: "rgb(26, 101, 255, 0.05)",
  },

  leftSideContainer: {
    display: "flex",
    flex: 1,
    alignItems: "center",
    position: "relative",
    width: "calc(100% - 60px)",
    paddingRight: "20px",
    overflow: "hidden",
    willChange: "transform",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
  },

  leftSide: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    alignItems: "center",
    padding: "13px 20px 13px 15px",
    height: "105px",
    boxSizing: "border-box",
    width: "100%",
    willChange: "transform",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
  },

  rightSideContainer: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    willChange: "transform",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
  },
});

/**
 * 実際にリスト内で表示される1件分のアイテムコンポーネント。
 *
 * ・チェックボックス、ロゴ、部署情報、意図キーワード、アクションボタン等の要素を含む。
 * ・`react-window` の描画対象となるが、内部の描画最適化のため `PureComponent` を継承。
 * ・`checked` 状態やユーザー操作に応じて描画が切り替わる。
 *
 * ※ 大量描画されるため、不要な再レンダリングが起きないよう注意すること。
 */
class KeymanListItem extends React.PureComponent {
  state = {
    disabled: false,
  };

  handleClick = () => {
    if (!this.state.disabled && this.props.onItemClicked) {
      this.props.handleSelectItem(
        this.props.item,
        this.props.index,
        !this.props.checked
      );
    }
  };

  togglePreventDialog = () => {
    const user = this.props;
    (isFreePlan(user)) ? this.props.togglePlanDialog() : this.props.togglePromotePlanChangeDialog()
  }

  /**
   * item.hot_scores から現在選択中のAI生成タグのスコアを取得
   * hot_scoresは{tag_name: hot_score}形式のため、タグ名を使用
   */
  getHotScore = () => {
    const { item, selectedAiTagName } = this.props;

    // AI生成タグが選択されていない場合はnull
    if (!selectedAiTagName) {
      return null;
    }

    // item.hot_scores がない場合はnull
    if (!item || !item.hot_scores) {
      return null;
    }

    // hot_scoresが文字列の場合はJSONパース（Elasticsearchから文字列で返される場合がある）
    let hotScores = item.hot_scores;
    if (typeof hotScores === 'string') {
      try {
        hotScores = JSON.parse(hotScores);
      } catch (e) {
        // パースに失敗した場合はnullを返す
        return null;
      }
    }

    // hot_scoresがオブジェクトでない場合はnull
    if (typeof hotScores !== 'object' || hotScores === null || Array.isArray(hotScores)) {
      return null;
    }

    // タグ名に対応するスコアを返す
    // hot_scoresは{tag_name: hot_score}形式
    const hotScore = hotScores[selectedAiTagName];

    // 数値でない場合はnull（0は有効なスコア）
    return hotScore !== undefined && hotScore !== null && typeof hotScore === 'number' ? hotScore : null;
  }

  render() {
    const {
      classes,
      onItemClicked,
      checked,
      index,
      item,
      handleSelectItem,
      user,
      disabled,
      filterIntentKeywords,
      enableExecutiveListAdvancedSearch,
      toggleExecDetailItemDialog,
      toggleCsvDownloadDialog,
      selectedAiTagName
    } = this.props;

    const hotScore = this.getHotScore();

    let style = this.props.style ? this.props.style : {};

    return (
      <Paper
        className={classNames(
          classes.root,
          checked ? classes.containerChecked : "",
          index !== 0 ? classes.hasBorderTop : "",
        )}
        elevation={0}
        style={{
          ...style,
          cursor: !this.state.disabled && onItemClicked ? "pointer" : "auto",
          pointerEvents: this.props.disabled ? "none" : "auto",
        }}
        onClick={this.handleClick}
      >
        <CheckBox
          checked={checked}
          item={item}
          index={index}
          handleSelectItem={handleSelectItem}
        />
        <div className={classes.leftSideContainer}>
          <div className={classes.leftSide}>
            <Logo item={item} />
            <KeymanAttribute
              item={item}
              disabled={disabled}
              hotScore={selectedAiTagName ? hotScore : null}
            />
            <IntentKeywords
              item={item}
              user={user}
              filterIntentKeywords={filterIntentKeywords}
            />
          </div>
          <div className={classes.rightSideContainer}>
            <Level item={item}/>
            <DepartmentTel item={item} user={user}/>
            <Actions
              item={item}
              user={user}
              enableExecutiveListAdvancedSearch={enableExecutiveListAdvancedSearch}
              toggleExecDetailItemDialog={toggleExecDetailItemDialog}
              toggleCsvDownloadDialog={toggleCsvDownloadDialog}
            />
          </div>
        </div>
      </Paper>
    );
  }
}

KeymanListItem.propTypes = {
  user: PropTypes.object,
  classes: PropTypes.object.isRequired,
  style: PropTypes.object,
  key: PropTypes.number,
  index: PropTypes.number.isRequired,
  item: PropTypes.object.isRequired,
  checked: PropTypes.bool.isRequired,
  filterIntentKeywords: PropTypes.array,
  handleSelectItem: PropTypes.func,
  onItemClicked: PropTypes.func,
  disabled: PropTypes.bool,
  enableExecutiveListAdvancedSearch: PropTypes.bool,
  toggleCsvDownloadDialog: PropTypes.func.isRequired,
  toggleExecDetailItemDialog: PropTypes.func.isRequired,
  togglePlanDialog: PropTypes.func.isRequired,
  togglePromotePlanChangeDialog: PropTypes.func.isRequired,
  // AI生成タグ関連
  selectedAiTagName: PropTypes.string,
};

KeymanListItem.defaultProps = {
}

export default withStyles(styles)(KeymanListItem);



// WEBPACK FOOTER //
// ./src/components/ExecutiveList/Spreadsheet/KeymanListItem.jsx